/* this might be a server component but is used by cms-components/Html */
'use client';

import {
  Table,
  TableBody,
  TableCaption,
  TableHead,
  TableHeader,
  TableRow,
} from '@packages/shared/src/ui/Table/Table.client';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import React from 'react';
import type { HtmlTableEditorType } from './types';

const stripSearchParamsFromAnchors = (html: string) =>
  html.replace(/<a\s+[^>]*href="([^"]+)"[^>]*>/gi, (match, href) => {
    try {
      const url = new URL(href);
      url.search = '';
      return match.replace(href, url.toString());
    } catch (error) {
      logger.info(`Failed to strip search params from anchor href "${href}": ${error}`);
      return match;
    }
  });

export const CmsTable = ({
  data: { field_margin: margin, field_padding: padding, table_json: tableJson, id: tableId },
}: {
  data: HtmlTableEditorType;
}) => {
  const { tableHead, tableBody, caption } = tableJson;
  const isBot = useIsBot();

  return (
    <Table style={{ ...(margin && { margin }), ...(padding && { padding }) }}>
      {caption && <TableCaption>{caption}</TableCaption>}
      {tableHead && (
        <TableHeader>
          <TableRow>
            {tableHead.map((headCell, index) => (
              <TableHead
                // eslint-disable-next-line react/no-array-index-key
                key={`headCell-${index}`}
                colSpan={headCell.colspan ? parseInt(headCell.colspan, 10) : undefined}
                dangerouslySetInnerHTML={{ __html: headCell.th }}
              />
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody>
        {tableBody.map((bodyRow, outerIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`bodyRow-${outerIndex}`} className="[&_td:first-child]:font-normal">
            {bodyRow.map((bodyCell, innerIndex) => {
              // this id is needed to apply a corresponding style
              const id = `Table${tableId}${outerIndex}${innerIndex}`;
              const { th = '', td = '', colspan, rowspan, style } = bodyCell;
              const sanitizedCell = isBot ? stripSearchParamsFromAnchors(td || th) : td || th;
              const TableCellElement = th ? 'th' : 'td';
              return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={`bodyCellFragment-${outerIndex}-${innerIndex}`}>
                  <TableCellElement
                    // eslint-disable-next-line react/no-array-index-key
                    key={`bodyCell-${outerIndex}-${innerIndex}`}
                    colSpan={colspan ? parseInt(colspan, 10) : undefined}
                    rowSpan={rowspan ? parseInt(rowspan, 10) : undefined}
                    dangerouslySetInnerHTML={{
                      __html: sanitizedCell,
                    }}
                    id={id}
                    className="border-color-grey-light border-2 px-1.5 py-1"
                  />
                  {/* use this style syntax to apply drupals legacy css style string (e.g. 'height:200px;width:300px;') on this cell */}
                  {style && (
                    <style>
                      {`
                      #${id} {
                        ${style}
                      }
                    `}
                    </style>
                  )}
                </React.Fragment>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
